import React, { useMemo } from "react";
import type { ReactElement, ComponentProps } from "react";

import { differenceInMonths } from "date-fns";
import { HStack, Text } from "native-base";
import type { Icon } from "native-base";

import { TickIcon, CloseIcon, WarningIcon } from "pianofunclub-shared/components/Other/Icons";

type HStackProps = ComponentProps<typeof HStack>;
type TextProps = ComponentProps<typeof Text>;
type IconProps = ComponentProps<typeof Icon>;

interface Props extends HStackProps {
  dbsExpirationDate?: string | null;
  dbsNumber?: string | null;
  iconProps?: IconProps;
  isTransparent?: boolean;
  showLongText?: boolean;
  textProps?: TextProps;
}

const DbsStatus = (props: Props): ReactElement | null => {
  const { dbsExpirationDate, dbsNumber, iconProps, isTransparent, showLongText, textProps } = props;

  const { color, endColor, icon, startColor, text, textColor } = useMemo(() => {
    const currentDate = new Date();
    if (dbsNumber && dbsExpirationDate) {
      const isExpired = currentDate.getTime() > new Date(dbsExpirationDate).getTime();
      const monthsTillExpiration = differenceInMonths(new Date(dbsExpirationDate), currentDate);
      if (monthsTillExpiration > 6) {
        return !isTransparent
          ? {
              endColor: "primary.600",
              icon: <TickIcon color="surface.50" size="6" {...iconProps} />,
              startColor: "primary.400",
              text: `${showLongText ? "DBS " : ""}Valid (${monthsTillExpiration} months)`,
              textColor: "surface.50",
            }
          : {
              color: "transparent",
              icon: <TickIcon color="primary.600" size="6" {...iconProps} />,
              text: `${showLongText ? "DBS " : ""}Valid (${monthsTillExpiration} months)`,
              textColor: "primary.600",
            };
      } else if (!isExpired) {
        return !isTransparent
          ? {
              endColor: "orange.500",
              icon: <TickIcon color="surface.50" size="6" {...iconProps} />,
              startColor: "orange.300",
              text: `${showLongText ? "DBS " : ""}Valid (${monthsTillExpiration} months)`,
              textColor: "surface.50",
            }
          : {
              color: "transparent",
              icon: <TickIcon color="orange.500" size="6" {...iconProps} />,
              text: `${showLongText ? "DBS " : ""}Valid (${monthsTillExpiration} months)`,
              textColor: "orange.500",
            };
      } else {
        return !isTransparent
          ? {
              endColor: "red.500",
              icon: <CloseIcon color="surface.50" size="6" {...iconProps} />,
              startColor: "red.300",
              text: `${showLongText ? "DBS " : ""}Expired`,
              textColor: "surface.50",
            }
          : {
              color: "transparent",
              icon: <CloseIcon color="red.500" size="6" {...iconProps} />,
              text: `${showLongText ? "DBS " : ""}Expired`,
              textColor: "red.500",
            };
      }
    } else {
      return !isTransparent
        ? {
            endColor: "surface.800",
            icon: <WarningIcon color="surface.50" size="6" {...iconProps} />,
            startColor: "surface.600",
            text: "No DBS",
            textColor: "surface.50",
          }
        : {
            color: "transparent",
            icon: <WarningIcon color="surface.800" size="6" {...iconProps} />,
            text: "No DBS",
            textColor: "surface.800",
          };
    }
  }, [dbsExpirationDate, dbsNumber, iconProps, isTransparent, showLongText]);

  return (
    <HStack
      bg={
        startColor && endColor
          ? {
              linearGradient: {
                colors: [startColor, endColor],
                end: [0.75, 1],
                start: [0.25, 0],
              },
            }
          : color
      }
      borderRadius="2xl"
      pl="3"
      pr="4"
      py="2"
      space="2"
      {...props}
    >
      {icon}
      <Text color={textColor} fontSize="md" {...textProps}>
        {text}
      </Text>
    </HStack>
  );
};

export default DbsStatus;
